import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule, DatePipe, TitleCasePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { MatTableModule } from '@angular/material/table';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { Encounter, Location, Practitioner, Bundle } from 'fhir/r4';
import { AdmittedPatientsComponent } from '../admitted-patients/admitted-patients.component';
import { DetailsBuilderObject } from '../detailz-viewz/details-builder.service';
import { EmptyStateComponent } from '../shared/empty-state/empty-state.component';
import { TableHeaderComponent } from '../table-header/table-header.component';
import { ChipsDirective } from '../chips.directive';
import { NaPipe } from '../shared/na.pipe';
import { DetailActionButton, DetailzViewzComponent } from '../detailz-viewz/detailz-viewz.component';
import { PatientAddressPipe } from '../shared/pipes/patient-address.pipe';
import { PatientAgePipe } from '../shared/pipes/patient-age.pipe';
import { PatientContactsPipe } from '../shared/pipes/patient-contacts.pipe';
import { PatientNamePipe } from '../shared/pipes/patient-name.pipe';
import { CodeableConcept2Pipe } from '../shared/codeable-concept2.pipe';
import { ReferenceDisplayDirective } from '../shared/reference-display.directive';
import { forkJoin, of, Observable, switchMap, map, catchError } from 'rxjs';
import { EmphasisDirective } from '../shared/directives/emphasis.directive';
import { CheckSheetComponent } from '../check-sheet/check-sheet.component';

@Component({
    selector: 'app-patient-encounters',
    standalone: true,
    imports: [
        CommonModule,
        RouterLink,
        MatTableModule,
        MatCardModule,
        MatButtonModule,
        MatIconModule,
        MatMenuModule,
        MatDividerModule,
        DatePipe,
        TitleCasePipe,
        EmptyStateComponent,
        TableHeaderComponent,
        ChipsDirective,
        EmphasisDirective,
        NaPipe,
        CodeableConcept2Pipe,
        ReferenceDisplayDirective
    ],
    providers: [PatientNamePipe, PatientContactsPipe, PatientAgePipe, PatientAddressPipe],
    templateUrl: './patient-encounters.component.html',
    styleUrls: ['../patient-observation/patient-observation.component.scss', './patient-encounters.component.scss']
})
export class PatientEncountersComponent extends AdmittedPatientsComponent implements OnInit, OnDestroy {
    // Override table columns - will be set dynamically in ngOnInit
    override displayedColumns: string[] = [];

    // Filters
    encounterFilters = new Map<string, string[]>([
        ['status', ['planned', 'arrived', 'triaged', 'in-progress', 'onleave', 'finished', 'cancelled', 'entered-in-error']],
        ['class', ['AMB', 'EMER', 'FLD', 'HH', 'IMP', 'ACUTE', 'NONAC', 'OBSENC', 'PRENC', 'SS', 'VR']]
    ]);

    // Details builder for dialog
    encounterDetailsBuilder: DetailsBuilderObject = {
        resourceName: 'Encounter',
        resourceIcon: 'local_hospital',
        specialHeader: {
            strongSectionKey: 'type',
            iconSectionKeys: ['status', 'class'],
            contentSectionKeys: ['period - start', 'subject']
        },
        groups: [
            {
                groupName: 'Identification',
                groupIcon: 'badge',
                groupKeys: ['status', 'class', 'type', 'priority']
            },
            {
                groupName: 'Period',
                groupIcon: 'schedule',
                groupKeys: ['period - start', 'period - end', 'period - duration']
            },
            {
                groupName: 'Context',
                groupIcon: 'person',
                groupKeys: ['subject', 'serviceProvider']
            },
            {
                groupName: 'Location',
                groupIcon: 'place',
                groupKeys: [] // Dynamically populated
            },
            {
                groupName: 'Participants',
                groupIcon: 'group',
                groupKeys: [] // Dynamically populated
            },
            {
                groupName: 'Reason',
                groupIcon: 'info',
                groupKeys: [] // Dynamically populated
            }
        ]
    };

    override ngOnInit(): void {
        // Compute permissions for 'encounter' resource
        this.computePermissions('encounter');

        // Set columns dynamically based on patient context
        const patientId = this.stateService.currentPatientId$.getValue();
        if (patientId) {
            // Patient-specific view - no need for patient column
            this.displayedColumns = ['period', 'status', 'location', 'actions'];
        } else {
            // Org-wide view - include patient column
            this.displayedColumns = ['period', 'status', 'subject', 'location', 'actions'];
        }

        // Load encounters
        this.loadEncounters();
    }

    private loadEncounters(): void {
        const patientId = this.stateService.currentPatientId$.getValue();

        // Determine data source based on patient context
        const encounters$ = patientId
            ? this.stateService.PatientResources.encounters
            : this.stateService.orgWideResources.encounters;

        const currentEncounters = encounters$.getValue();

        if (!currentEncounters || currentEncounters.length === 0) {
            // Fetch from backend if not in state
            console.log('Encounters not found in state, fetching from backend...');

            const fetchUrl = patientId
                ? `${this.backendUrl}/Encounter?subject=Patient/${patientId}&_count=1000`
                : `${this.backendUrl}/Encounter?_count=1000`;

            this.http.get<Bundle<Encounter>>(fetchUrl)
                .pipe(
                    switchMap((bundle) => {
                        console.log(`Fetched ${bundle.entry?.length || 0} encounters from backend`);

                        // Persist to state
                        if (!patientId) {
                            this.stateService.processOrgWideBundleTransaction(bundle);
                        } else {
                            this.stateService.processBundleTransaction(bundle);
                        }

                        // Extract encounters
                        const encounters = (bundle.entry || [])
                            .map(e => ({ actualResource: e.resource as Encounter }))
                            .filter(r => r.actualResource);

                        return this.loadAndFlattenRelatedResources(encounters);
                    }),
                    catchError((err) => {
                        console.error('Failed to load encounters:', err);
                        this.errorService.openandCloseError('Failed to load encounters');
                        return of([]);
                    })
                )
                .subscribe((flattenedEncounters) => {
                    this.initiateDataSourceSubscription(of(flattenedEncounters));
                    this.applyFiltersAndTransforms();
                });
        } else {
            // Use existing data
            console.log('Using existing encounters from state');

            this.loadAndFlattenRelatedResources(currentEncounters)
                .subscribe((flattenedEncounters) => {
                    this.initiateDataSourceSubscription(of(flattenedEncounters));
                    this.applyFiltersAndTransforms();
                });
        }
    }

    /**
     * Load all related resources (Locations, Practitioners) and flatten data
     */
    private loadAndFlattenRelatedResources(
        encounters: Array<{ actualResource: Encounter }>
    ): Observable<Array<{ actualResource: Encounter & Record<string, any> }>> {
        if (!encounters.length) return of([]);

        // Extract all referenced resource IDs
        const locationIds = this.extractLocationIds(encounters);
        const practitionerIds = this.extractParticipantIds(encounters);

        // Fetch all related resources in parallel
        return forkJoin({
            locations: this.fetchLocations(locationIds),
            practitioners: this.fetchPractitioners(practitionerIds)
        }).pipe(
            map(({ locations, practitioners }) => {
                // Flatten and merge related data into each encounter
                return encounters.map(encounter => ({
                    actualResource: this.flattenEncounterData(
                        encounter.actualResource,
                        locations,
                        practitioners
                    )
                }));
            })
        );
    }

    /**
     * Extract location IDs from encounter location references
     */
    private extractLocationIds(encounters: Array<{ actualResource: Encounter }>): string[] {
        const ids = new Set<string>();
        encounters.forEach(encounter => {
            const locations = encounter.actualResource.location || [];
            locations.forEach(loc => {
                const id = this.extractIdFromReference(loc.location?.reference);
                if (id) ids.add(id);
            });
        });
        return Array.from(ids);
    }

    /**
     * Extract practitioner IDs from participant references
     */
    private extractParticipantIds(encounters: Array<{ actualResource: Encounter }>): string[] {
        const ids = new Set<string>();
        encounters.forEach(encounter => {
            const participants = encounter.actualResource.participant || [];
            participants.forEach(part => {
                const id = this.extractIdFromReference(part.individual?.reference);
                if (id && part.individual?.reference?.startsWith('Practitioner/')) ids.add(id);
            });
        });
        return Array.from(ids);
    }

    /**
     * Extract ID from FHIR reference string
     */
    private extractIdFromReference(ref?: string): string | null {
        if (!ref) return null;
        const parts = ref.split('/');
        return parts.length > 1 ? parts.pop() || null : null;
    }

    /**
     * Fetch locations by IDs
     */
    private fetchLocations(ids: string[]): Observable<Location[]> {
        if (!ids.length) return of([]);

        const idParams = ids.join(',');
        return this.http.get<Bundle<Location>>(`${this.backendUrl}/Location?_id=${idParams}`).pipe(
            map(bundle => (bundle.entry || []).map(e => e.resource as Location).filter(Boolean)),
            catchError(err => {
                console.error('Failed to fetch locations:', err);
                return of([]);
            })
        );
    }

    /**
     * Fetch practitioners by IDs
     */
    private fetchPractitioners(ids: string[]): Observable<Practitioner[]> {
        if (!ids.length) return of([]);

        const idParams = ids.join(',');
        return this.http.get<Bundle<Practitioner>>(`${this.backendUrl}/Practitioner?_id=${idParams}`).pipe(
            map(bundle => (bundle.entry || []).map(e => e.resource as Practitioner).filter(Boolean)),
            catchError(err => {
                console.error('Failed to fetch practitioners:', err);
                return of([]);
            })
        );
    }

    hasActiveAdmission = (encounterRes: Encounter) => {

        const encounter = encounterRes;

        // Check if encounter is in-progress
        const isInProgress = encounter.status === 'in-progress';

        // Check if encounter class is inpatient (IMP)
        const isInpatient = encounter.class?.code?.toUpperCase() === 'IMP' ||
            encounter.class?.display?.toLowerCase().includes('inpatient');

        // Check if hospitalization data exists (indicates admission)
        const hasHospitalization = !!encounter.hospitalization;

        // Additional check: has admission identifier
        const hasAdmissionId = encounter.hospitalization?.preAdmissionIdentifier?.value ||
            encounter.identifier?.some((id: any) =>
                id.system?.includes('admission-id')
            );

        const isActive = isInProgress && isInpatient && hasAdmissionId;

        if (isActive) {
            console.log('🚫 Active admission found:', {
                encounterId: encounter.id,
                status: encounter.status,
                class: encounter.class,
                admissionId: encounter.hospitalization?.preAdmissionIdentifier?.value,
                hasHospitalization
            });
        }

        return isActive;
    }

    /**
     * Flatten encounter data with related resources
     */
    private flattenEncounterData(
        encounter: Encounter,
        locations: Location[],
        practitioners: Practitioner[]
    ): Encounter & Record<string, any> {
        const flattened: any = { ...encounter };

        // Flatten period
        if (encounter.period) {
            if (encounter.period.start) {
                flattened['period - start'] = new Date(encounter.period.start).toLocaleString();
            }
            if (encounter.period.end) {
                flattened['period - end'] = new Date(encounter.period.end).toLocaleString();
            }
            if (encounter.period.start && encounter.period.end) {
                const duration = new Date(encounter.period.end).getTime() - new Date(encounter.period.start).getTime();
                const hours = Math.floor(duration / (1000 * 60 * 60));
                const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));
                flattened['period - duration'] = `${hours}h ${minutes}m`;
            }
        }

        // Flatten locations
        const locationRefs = encounter.location || [];
        locationRefs.forEach((locRef, index) => {
            const locId = this.extractIdFromReference(locRef.location?.reference);
            const loc = locations.find(l => l.id === locId);

            if (loc) {
                const n = index + 1;
                flattened[`location ${n} - name`] = loc.name || 'N/A';
                flattened[`location ${n} - type`] = loc.type?.[0]?.coding?.[0]?.display ||
                    loc.type?.[0]?.coding?.[0]?.code || 'N/A';

                if (locRef.period?.start) {
                    flattened[`location ${n} - from`] = new Date(locRef.period.start).toLocaleString();
                }
                if (locRef.period?.end) {
                    flattened[`location ${n} - to`] = new Date(locRef.period.end).toLocaleString();
                }
            }
        });

        // Flatten participants
        const participantRefs = encounter.participant || [];
        participantRefs.forEach((partRef, index) => {
            const practId = this.extractIdFromReference(partRef.individual?.reference);
            const pract = practitioners.find(p => p.id === practId);

            if (pract) {
                const n = index + 1;
                const name = pract.name?.[0];
                if (name) {
                    const fullName = [
                        ...(name.prefix || []),
                        ...(name.given || []),
                        name.family
                    ].filter(Boolean).join(' ');
                    flattened[`participant ${n} - name`] = fullName || 'N/A';
                } else {
                    flattened[`participant ${n} - name`] = partRef.individual?.display || 'N/A';
                }

                if (partRef.type?.[0]) {
                    flattened[`participant ${n} - role`] = partRef.type[0].text ||
                        partRef.type[0].coding?.[0]?.display || 'N/A';
                }
            }
        });

        // Flatten reason codes
        const reasonCodes = encounter.reasonCode || [];
        reasonCodes.forEach((reason, index) => {
            const n = index + 1;
            flattened[`reason ${n} - code`] = reason.coding?.[0]?.code || 'N/A';
            flattened[`reason ${n} - display`] = reason.text || reason.coding?.[0]?.display || 'N/A';
        });

        // Add display fields
        flattened['class'] = encounter.class?.code || encounter.class?.display || 'N/A';
        flattened['type'] = encounter.type?.[0]?.text || encounter.type?.[0]?.coding?.[0]?.display || 'N/A';
        flattened['priority'] = encounter.priority?.text || encounter.priority?.coding?.[0]?.display || 'N/A';

        // Location summary (first location name)
        if (locationRefs.length > 0) {
            const firstLocId = this.extractIdFromReference(locationRefs[0].location?.reference);
            const firstLoc = locations.find(l => l.id === firstLocId);
            flattened['location'] = firstLoc?.name || locationRefs[0].location?.display || 'N/A';
        } else {
            flattened['location'] = 'N/A';
        }
        flattened['isAdmitted'] = this.hasActiveAdmission(encounter);
        return flattened;
    }

    /**
     * Apply filters and transforms
     */
    private applyFiltersAndTransforms(): void {
        // Filter out entered-in-error
        this.applyFilter('validity', (row) => row['status'] !== 'entered-in-error');

        // Sort by period start (descending - newest first)
        this.sortData(['period - start'], false);

        // Transform data for display
        this.transformData((row) => ({
            ...row,
            periodDisplay: row['period - start'] ? new Date(row['period - start']).toLocaleDateString() : 'N/A',
            classDisplay: row['class'] || 'N/A'
        }));

        // Organize search filters
        this.organizingSearchFilters(this.encounterFilters);
    }

    /**
     * Show encounter details
     */
    showRow(encounter: Encounter & Record<string, any>): void {
        this.showEncounterDetails(encounter);
    }

    /**
     * Show encounter details dialog
     */
    showEncounterDetails(encounter: Encounter & Record<string, any>): void {
        // Build dynamic groups based on available data
        const builder = { ...this.encounterDetailsBuilder };

        // Populate Location group keys
        const locationKeys = Object.keys(encounter).filter(k => k.startsWith('location '));
        if (locationKeys.length > 0) {
            const locationGroup = builder.groups.find(g => g.groupName === 'Location');
            if (locationGroup) locationGroup.groupKeys = locationKeys;
        }

        // Populate Participants group keys
        const participantKeys = Object.keys(encounter).filter(k => k.startsWith('participant '));
        if (participantKeys.length > 0) {
            const participantsGroup = builder.groups.find(g => g.groupName === 'Participants');
            if (participantsGroup) participantsGroup.groupKeys = participantKeys;
        }

        // Populate Reason group keys
        const reasonKeys = Object.keys(encounter).filter(k => k.startsWith('reason '));
        if (reasonKeys.length > 0) {
            const reasonGroup = builder.groups.find(g => g.groupName === 'Reason');
            if (reasonGroup) reasonGroup.groupKeys = reasonKeys;
        }

        const dialogRef = this.dialog.open(DetailzViewzComponent, {
            maxHeight: '93vh',
            maxWidth: '650px',
            width: '100%',
            data: {
                resourceData: encounter,
                detailsBuilderObject: builder,
                actionButtons: this.buildEncounterActions(encounter)
            }
        });

        const sub = dialogRef.componentInstance?.actionInvoked.subscribe(({ key }) => {
            this.handleDetailAction(key, encounter);
        });

        dialogRef.afterClosed().subscribe(() => sub?.unsubscribe());
    }

    /**
     * Build action buttons for encounter details
     */
    private buildEncounterActions(encounter: Encounter & Record<string, any>): DetailActionButton[] {
        const actions: DetailActionButton[] = [];

        // View full encounter action
        actions.push({
            key: 'viewFullEncounter',
            label: 'View Full Encounter',
            icon: 'visibility',
            capabilities: [{ resource: 'encounter', action: 'viewAll' }]
        });

        // Update status action (only for active encounters)
        if (['planned', 'arrived', 'triaged', 'in-progress'].includes(encounter['status'])) {
            // actions.push({
            //     key: 'updateStatus',
            //     label: 'Update Status',
            //     icon: 'update',
            //     capabilities: [{ resource: 'encounter', action: 'update' }]
            // });
        }

        return actions;
    }

    /**
     * Handle action button clicks
     */
    private handleDetailAction(actionKey: string, encounter: Encounter & Record<string, any>): void {
        switch (actionKey) {
            case 'viewFullEncounter':
                console.log('View full encounter:', encounter);
                this.dialog.open(CheckSheetComponent, {
                    maxHeight: '90vh',
                    maxWidth: '940px',
                    autoFocus: false,
                    data: {
                        encounterId: encounter.id,
                        patientId: encounter['patientId'] || this.stateService.currentPatientId$.getValue()
                    }
                });
                break;

            case 'updateStatus':
                console.log('Update encounter status:', encounter);
                // TODO: Open update status dialog
                break;

            default:
                console.warn('Unknown action key:', actionKey);
                break;
        }
    }

    override ngOnDestroy(): void {
        console.log('Cleaning up patient encounters');
        super.ngOnDestroy?.();
    }
    onExportEncounters() {
        console.log('Exporting encounters');
    }
}
